import {ActionTree} from 'vuex';
import {IRootState} from '../root-state.store';
import {IProfileState} from './profile-state.store';
import {
  PROFILE_CHANGE_EMAIL,
  PROFILE_GET_ENTITY_ROLES,
  PROFILE_GET_MEMBERS,
  PROFILE_SET_MEMBERS,
  PROFILE_SET_ENTITY_ROLES,
  PROFILE_SET_TOTAL_PAGES,
  PROFILE_FILTER_CHANGE,
  PROFILE_SET_CURRENT_FILTER,
  PROFILE_INIT_MEMBERS,
  PROFILE_RECORD_MEMBER,
  PROFILE_INIT_MEMBER,
  PROFILE_RESET_MEMBER,
  PROFILE_RESET_MEMBERS,
  PROFILE_GET_ALL_ENTITY_ROLES,
  PROFILE_SET_INTERNAL_ROLES,
  PROFILE_SET_EXTERNAL_ROLES,
  PROFILE_UPDATE_MEMBER,
  PROFILE_DELETE_MEMBER,
  PROFILE_EDIT_MEMBER,
  PROFILE_ACTION,
  PROFILE_DELETE_MEMBERS,
  PROFILE_GET_CIVILITY_LEGAL_FORM,
  PROFILE_SET_CIVILITY_LEGAL_FORM, PROFILE_UPDATE_SIGNATURE, PROFILE_GET_CONTACT, PROFILE_INIT_CURRENT_MEMBER
} from './profile-types.store';
import {IProfileService, ISearchService} from '../../services';
import {CrudStatus} from '../../common/enums';
import {ITeamMember} from '../../models/profile/member.model';
import {AddressDto} from '../../models/address/address.model';
import {IUser} from '../../models/user/user.model';
import {USER_INFO_FIELD} from "../user/user-types.store";

export const actions: ActionTree<IProfileState, IRootState> = {
  [PROFILE_CHANGE_EMAIL]: async ({commit, state}, payload: { email: string, password: string }) => {
    return await state.service!.changeEmail(payload.email, payload.password)
      .then(res => {
        return res
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [PROFILE_GET_ENTITY_ROLES]: async ({commit, state}, params) => {
    return state!.service.getEntityRoles(params)
      .then(result => {
        commit(PROFILE_SET_ENTITY_ROLES, result)
        return Promise.resolve(result)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_GET_ALL_ENTITY_ROLES]: async ({commit, state}) => {
    // if(state.entity_roles.length) return Promise.resolve()
    return state!.service.getAllEntityRoles()
      .then(result => {
        commit(PROFILE_SET_INTERNAL_ROLES, result['internal'])
        commit(PROFILE_SET_EXTERNAL_ROLES, result['external'])
        commit(PROFILE_SET_ENTITY_ROLES, result['all'])
        return Promise.resolve(result)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_GET_CIVILITY_LEGAL_FORM]: async ({commit, state}): Promise<any> => {
    if (state.civilities.length) return Promise.resolve()
    return state!.service.getCivilityLegalForm()
      .then(response => {
        commit(PROFILE_SET_CIVILITY_LEGAL_FORM,
          response.map(item => ({id: item.id, text: item.shorten || item.name})).filter(item => item.text))
        return Promise.resolve()
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_GET_MEMBERS]: async ({commit, getters}, payload: { $skip: number, $limit:number }) => {
    const {$skip, $limit} = payload
    return getters!.service.getMembers(getters.memberParameter, $skip, $limit)
      .then(result => {
        commit(PROFILE_SET_MEMBERS, result)
        commit(PROFILE_SET_TOTAL_PAGES, result.total)
        return Promise.resolve(result)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_GET_CONTACT]: async ({commit, getters}, payload: any) => {
    return getters!.service.getContacts(payload)
  },

  [PROFILE_RESET_MEMBERS]: async ({commit}) => {
    commit(PROFILE_INIT_MEMBERS)
    return Promise.resolve(true)
  },
  [PROFILE_FILTER_CHANGE]: async ({commit, state}, newFilter: number[]) => {
    commit(PROFILE_SET_CURRENT_FILTER, newFilter)
    commit(PROFILE_INIT_MEMBERS)
    return Promise.resolve(state.current_filter)
  },
  [PROFILE_RECORD_MEMBER]: async ({commit, getters}) => {
    return getters!.service.recordMember(getters.member)
      .then(response => {
        commit(PROFILE_INIT_MEMBER, getters!.service.initMember())
        return Promise.resolve(response)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_UPDATE_MEMBER]: async ({ commit, getters, rootGetters }, payload: { own: boolean } = {own: false}): Promise<any> => {
    const service: IProfileService = getters.service
    const member: ITeamMember | IUser = payload.own ? rootGetters['user/user'] : getters.member
    if (payload.own) {
      const currentMember = rootGetters['user/userInfo']
      commit(PROFILE_INIT_CURRENT_MEMBER, getters.service.initMember({
        ...currentMember,
        civility: currentMember.fk_entity_civility_legal_form,
      }))
    }

    return service.updateMember(member.id, member, payload.own, getters.currentMember)
      .then(updatedMember => {
        if (!payload.own) {
          commit(PROFILE_INIT_MEMBER, service.initMember())
          commit(PROFILE_ACTION, CrudStatus.NoAction)
        }
        return Promise.resolve(updatedMember)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_UPDATE_SIGNATURE]: async ({commit, getters, rootGetters}, payload: { signature: string }): Promise<any> => {
    const service: IProfileService = getters.service
    const currentUserId = rootGetters['user/user'].id
    return service.updateMySignature(currentUserId, payload)
      .then(response => {
        commit(PROFILE_ACTION, CrudStatus.NoAction)
        return Promise.resolve(response)
      })
      .catch(err => Promise.reject(err))
  },
  [PROFILE_DELETE_MEMBER]: async ({commit, getters}): Promise<any> => {
    const service: IProfileService = getters.service
    return service.deleteMember(getters.memberSelection[0])
      .then(deletedMember => {
        return Promise.resolve(deletedMember)
      })
      .catch(err => Promise.reject(err))
      .finally(() => commit(PROFILE_ACTION, CrudStatus.NoAction))
  },
  [PROFILE_DELETE_MEMBERS]: async ({commit, getters}): Promise<any> => {
    const service: IProfileService = getters.service
    const memberSelection: number[] = getters.memberSelection
    return Promise.all(memberSelection.map(selection => service.deleteMember(selection)))
      .then(deletedMembers => {
        return Promise.resolve(deletedMembers)
      })
      .catch(err => Promise.reject(err))
      .finally(() => commit(PROFILE_ACTION, CrudStatus.NoAction))
  },
  [PROFILE_RESET_MEMBER]: async ({commit, getters}) => {
    commit(PROFILE_ACTION, CrudStatus.NoAction)
    commit(PROFILE_INIT_MEMBER, getters!.service.initMember())
  },
  [PROFILE_EDIT_MEMBER]: async ({commit, getters, rootGetters}, memberId: number) => {

    const searchService: ISearchService = rootGetters['search/service']
    searchService.searchEntityById(memberId)
      .then(member => {
        const {address} = member
        if (member.legal_entity) var {legal_entity: {ape: code_ape, tva: num_tva, siret: num_siret}} = member
        const profileService: IProfileService = getters.service
        commit(PROFILE_ACTION, CrudStatus.Update)
        commit(PROFILE_INIT_MEMBER, profileService.initMember({
          ...member,
          address: address ? new AddressDto(address) : new AddressDto,
          ...{code_ape, num_tva, num_siret},
          civility: member.fk_entity_civility_legal_form,
          specialization: member.problem_solver,
          ...member.parent_entity && {
            parent_entity: {
              ...member.parent_entity,
              lastname: member.parent_entity.name
            }
          },
          //... member.blocks && { blocks: (member.blocks as any[]).map(block=> block.id) },
          //... member.apartments && { apartments: (member.apartments as any[]).map(apartment=> apartment.id) }
        }))
        commit(PROFILE_INIT_CURRENT_MEMBER, profileService.initMember({
          ...member,
          address: address ? new AddressDto(address) : new AddressDto,
          ...{code_ape, num_tva, num_siret},
          civility: member.fk_entity_civility_legal_form,
          specialization: member.problem_solver,
          ...member.parent_entity && {
            parent_entity: {
              ...member.parent_entity,
              lastname: member.parent_entity.name
            }
          },
        }))

        return Promise.resolve(member)
      })
      .catch(err => Promise.reject(err))
  }
}



// WEBPACK FOOTER //
// ./src/store/profile/profile-actions.store.ts